import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { IdCard, SlidersHorizontal } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchFeatureFlags, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A14.12 — Remote config qiymatlari. A14.01 (flag ro'yxati) bilan bir manba
 * (`GET /admin/settings/flags`), lekin bu ekran KONFIG-QIYMATIga qaraydi:
 * qiymat turini aniqlaydi (bool/son/JSON/matn) va payload'ni formatlab ko'rsatadi.
 * Read-only (yozuv = A14.03/12-edit, `POST /admin/settings/flags` kutadi = DEPS).
 */
type ValueType = 'bool' | 'number' | 'json' | 'text'

function detectType(v: string): ValueType {
  const s = v.trim()
  if (s === 'true' || s === 'false') return 'bool'
  if (s !== '' && !Number.isNaN(Number(s))) return 'number'
  if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
    try {
      JSON.parse(s)
      return 'json'
    } catch {
      return 'text'
    }
  }
  return 'text'
}

function formatValue(v: string, type: ValueType): string {
  if (type !== 'json') return v
  try {
    return JSON.stringify(JSON.parse(v))
  } catch {
    return v
  }
}

export function RemoteConfigValues() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminFeatureFlag>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/flags/${encodeURIComponent(r.key)}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminFeatureFlag>[]>(
    () => [
      {
        key: 'key',
        header: t.flags.key,
        type: 'text',
        minWidth: 240,
        render: (r) => <span className="font-mono text-sm text-primary">{r.key}</span>,
      },
      {
        key: 'type',
        header: t.flags.type,
        type: 'enum',
        width: 110,
        accessor: (r) => detectType(r.value),
        options: [
          { value: 'bool', label: 'bool', status: 'active' },
          { value: 'number', label: 'number', status: 'pending' },
          { value: 'json', label: 'json', status: 'review' },
          { value: 'text', label: 'text', status: 'expired' },
        ],
      },
      {
        key: 'value',
        header: t.flags.value,
        type: 'longtext',
        minWidth: 260,
        render: (r) => {
          const type = detectType(r.value)
          return <span className="font-mono text-xs text-secondary">{formatValue(r.value, type)}</span>
        },
      },
      {
        key: 'enabled',
        header: t.col.status,
        type: 'enum',
        width: 120,
        accessor: (r) => (r.enabled ? 'on' : 'off'),
        options: [
          { value: 'on', label: t.flags.on, status: 'active' },
          { value: 'off', label: t.flags.off, status: 'deleted' },
        ],
      },
      { key: 'updated_at', header: t.finance.updated, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchFeatureFlags, [])

  return (
    <>
      <PageHeader title={t.flags.remoteTitle} screenId="A14.12" />
      <DataTable<AdminFeatureFlag>
        screenId="A14.12"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.key}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <SlidersHorizontal size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
